import type {
  ApiError,
  BillingInfo,
  CampaignDetail,
  CheckoutSessionResponse,
  CreateCampaignRequest,
  CreateCampaignResponse,
  CreateGenerationRequest,
  CreateGenerationResponse,
  EditRoadmapPhaseRequest,
  GenerationDetail,
  LoginResponse,
  PaginatedCampaigns,
  PaginatedGenerations,
  SubscriptionPlan,
  UserMe,
} from './types';
import { ApiClientError } from './types';
import { isPublicPath } from '../lib/public-paths';
import { navigate } from '../router';

const AUTH_PUBLIC_PREFIXES = ['/api/auth/'];

function shouldRedirectToLogin(apiPath: string): boolean {
  if (AUTH_PUBLIC_PREFIXES.some((prefix) => apiPath.startsWith(prefix))) {
    return false;
  }
  return !isPublicPath(location.pathname);
}

async function redirectToLogin(): Promise<void> {
  await navigate('/login', true);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = `Error ${response.status}`;
    let code: string | undefined;
    try {
      const body = (await response.json()) as ApiError;
      if (body.error) message = body.error;
      if (body.code) code = body.code;
    } catch {
      // ignore parse errors
    }

    if (response.status === 401 && shouldRedirectToLogin(path)) {
      await redirectToLogin();
    }

    throw new ApiClientError(message, response.status, code);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ============================================================
// Auth
// ============================================================

export async function login(email: string): Promise<LoginResponse> {
  return request<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function getMe(): Promise<UserMe> {
  return request<UserMe>('/api/user/me');
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    await getMe();
    return true;
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      return false;
    }
    throw err;
  }
}

export async function logout(): Promise<void> {
  try {
    await request<{ ok: true }>('/api/auth/logout', { method: 'POST' });
  } finally {
    await navigate('/login', true);
  }
}

// ============================================================
// Generations
// ============================================================

export async function listGenerations(
  page = 1,
  limit = 20,
): Promise<PaginatedGenerations> {
  return request<PaginatedGenerations>(
    `/api/generate?page=${page}&limit=${limit}`,
  );
}

export async function getGeneration(id: string): Promise<GenerationDetail> {
  return request<GenerationDetail>(`/api/generate/${id}`);
}

export async function createGeneration(
  body: CreateGenerationRequest,
): Promise<CreateGenerationResponse> {
  return request<CreateGenerationResponse>('/api/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteGeneration(id: string): Promise<void> {
  return request<void>(`/api/generate/${id}`, { method: 'DELETE' });
}

export async function recoverGeneration(
  id: string,
): Promise<{ generationId: string; recovered: boolean }> {
  return request<{ generationId: string; recovered: boolean }>(
    `/api/generate/${id}/recover`,
    { method: 'POST' },
  );
}

// ============================================================
// Campaigns
// ============================================================

export async function listCampaigns(
  page = 1,
  limit = 20,
): Promise<PaginatedCampaigns> {
  return request<PaginatedCampaigns>(
    `/api/campaigns?page=${page}&limit=${limit}`,
  );
}

export async function getCampaign(id: string): Promise<CampaignDetail> {
  return request<CampaignDetail>(`/api/campaigns/${id}`);
}

export async function createCampaign(
  body: CreateCampaignRequest,
): Promise<CreateCampaignResponse> {
  return request<CreateCampaignResponse>('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function editRoadmapPhase(
  campaignId: string,
  phase: number,
  body: EditRoadmapPhaseRequest,
): Promise<{ ok: true }> {
  return request<{ ok: true }>(
    `/api/campaigns/${campaignId}/roadmap/${phase}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

export async function generateCampaignPost(
  campaignId: string,
  phase: number,
): Promise<{ generationId: string }> {
  return request<{ generationId: string }>(
    `/api/campaigns/${campaignId}/generate/${phase}`,
    { method: 'POST' },
  );
}

export async function generateAllCampaignPosts(
  campaignId: string,
): Promise<{ generations: Array<{ phase: number; generationId: string }> }> {
  return request<{ generations: Array<{ phase: number; generationId: string }> }>(
    `/api/campaigns/${campaignId}/generate-all`,
    { method: 'POST' },
  );
}

export async function deleteCampaign(id: string): Promise<void> {
  return request<void>(`/api/campaigns/${id}`, { method: 'DELETE' });
}

// ============================================================
// Billing
// ============================================================

export async function getBillingInfo(): Promise<BillingInfo> {
  return request<BillingInfo>('/api/billing/info');
}

export async function createSubscriptionCheckout(
  plan: SubscriptionPlan,
): Promise<CheckoutSessionResponse> {
  return request<CheckoutSessionResponse>('/api/billing/checkout/subscription', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
}

export async function createCreditsCheckout(): Promise<{ url: string }> {
  return request<{ url: string }>('/api/billing/checkout/credits', {
    method: 'POST',
  });
}

export async function createPortalSession(): Promise<{ url: string }> {
  return request<{ url: string }>('/api/billing/portal', {
    method: 'POST',
  });
}
